import React, { useCallback, useState, useMemo, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DATbgcard from './assets/DATbgcard.png';
import BlankBG from './assets/BlankBG.png';
import DownArrow from './assets/downArrow.png';
import LikeIcon from './assets/LikeIcon.png';
import LikeIconActive from './assets/LikeIconActive.png';
import { AppContext } from './AppContext';
import { firestore } from './firebase';
import { runTransaction, doc } from 'firebase/firestore';

const width = window.innerWidth;
const height = window.innerHeight;
const bottomAreaHeight = height * 0.19;
const scrollAreaTopPadding = height * 0;

// normalize releaseTimestamp (supports number | string | Date | Firestore Timestamp)
const rtMillis = (ts) => {
  if (!ts) return 0;
  if (typeof ts === 'number') return ts;
  if (typeof ts === 'string') {
    const n = Date.parse(ts);
    return Number.isNaN(n) ? 0 : n;
  }
  if (ts?.seconds != null) return ts.seconds * 1000 + Math.floor((ts.nanoseconds || 0) / 1e6);
  if (typeof ts?.toMillis === 'function') return ts.toMillis();
  if (typeof ts?.toDate === 'function') {
    try { return ts.toDate().getTime() || 0; } catch { return 0; }
  }
  if (ts instanceof Date) return ts.getTime();
  return 0;
};


const MusicHome = ({ assets, onPlayVideo, isVideoPlaying, onMoveMP }) => {
  const navigate = useNavigate();
  const {
    setUserWallet,
    userWallet,
    setAssetsSummary,
    userId2,
    assetsSummary,
  } = useContext(AppContext);

const [selectedTimeRange, setSelectedTimeRange] = useState('30days');

  const [sortOption, setSortOption] = useState('salesVolume');
  const [showMoreCollections, setShowMoreCollections] = useState(false);

  const getFloorPrice = useCallback((asset) => {
    if (!asset?.editions) return 0;
    const forSaleEditions = asset.editions.filter((ed) => ed.isForSale);
    if (!forSaleEditions.length) return 0;
    return forSaleEditions.reduce(
      (lowest, ed) => (ed.price < lowest ? ed.price : lowest),
      forSaleEditions[0].price
    );
  }, []);

const getVolumeForRange = useCallback(
  (asset) => {
    if (!asset || !asset.editions?.length) return { current: 0, previous: 0 };

    const now = new Date();

    // helper to build YYYY-MM-DD keys in UTC
    const fmtDay = (d) => {
      const y  = d.getUTCFullYear();
      const m  = String(d.getUTCMonth() + 1).padStart(2, '0');
      const dd = String(d.getUTCDate()).padStart(2, '0');
      return `${y}-${m}-${dd}`;
    };

    // today / 7days behavior kept for backward compat
    if (selectedTimeRange === 'today' || selectedTimeRange === '7days') {
      let currentKey, previousKey;
      if (selectedTimeRange === 'today') {
        const y = now.getFullYear();
        const m = String(now.getMonth() + 1).padStart(2, '0');
        const d = String(now.getDate()).padStart(2, '0');
        currentKey = `${y}-${m}-${d}`;
        const yest = new Date(now);
        yest.setDate(now.getDate() - 1);
        const yy = yest.getFullYear();
        const ym = String(yest.getMonth() + 1).padStart(2, '0');
        const yd = String(yest.getDate()).padStart(2, '0');
        previousKey = `${yy}-${ym}-${yd}`;
      } else {
        const getISOWeek = (date) => {
          const target = new Date(date.valueOf());
          const dayNr = (date.getUTCDay() + 6) % 7;
          target.setUTCDate(target.getUTCDate() - dayNr + 3);
          const firstThursday = target.valueOf();
          target.setUTCMonth(0, 1);
          if (target.getUTCDay() !== 4) {
            target.setUTCMonth(0, 1 + ((4 - target.getUTCDay()) + 7) % 7);
          }
          return 1 + Math.ceil((firstThursday - target) / 604800000);
        };
        const yy = now.getUTCFullYear();
        const wn = getISOWeek(now);
        currentKey = `${yy}-W${String(wn).padStart(2, '0')}`;
        const lastWeek = new Date(now);
        lastWeek.setUTCDate(now.getUTCDate() - 7);
        const ly = lastWeek.getUTCFullYear();
        const lwn = getISOWeek(lastWeek);
        previousKey = `${ly}-W${String(lwn).padStart(2, '0')}`;
      }

      let current = 0, previous = 0;
      asset.editions.forEach((ed) => {
        const e = ed.earnings || {};
        if (selectedTimeRange === 'today') {
          current  += e.daily?.[currentKey]  || 0;
          previous += e.daily?.[previousKey] || 0;
        } else {
          current  += e.weekly?.[currentKey]  || 0;
          previous += e.weekly?.[previousKey] || 0;
        }
      });
      return { current, previous };
    }

    // 30/60/90 days: rolling sums across DAILY keys
    if (selectedTimeRange === '30days' || selectedTimeRange === '60days' || selectedTimeRange === '90days') {
      const days = selectedTimeRange === '60days' ? 60 : selectedTimeRange === '90days' ? 90 : 30;

      const currentDates = new Set();
      for (let i = 0; i < days; i++) {
        const d = new Date(now);
        d.setUTCDate(d.getUTCDate() - i);
        currentDates.add(fmtDay(d));
      }

      const previousDates = new Set();
      for (let i = days; i < days * 2; i++) {
        const d = new Date(now);
        d.setUTCDate(d.getUTCDate() - i);
        previousDates.add(fmtDay(d));
      }

      let current = 0, previous = 0;
      asset.editions.forEach((ed) => {
        const daily = (ed.earnings || {}).daily || {};
        for (const [dateKey, amt] of Object.entries(daily)) {
          if (currentDates.has(dateKey)) current += amt;
          else if (previousDates.has(dateKey)) previous += amt;
        }
      });

      return { current, previous };
    }

    return { current: 0, previous: 0 };
  },
  [selectedTimeRange]
);


  const collectionData = useMemo(() => {
    if (!assets?.length) return [];

    const now = new Date();
let currentKey, previousKey;
if (selectedTimeRange === 'today') {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  currentKey = `${y}-${m}-${d}`;
  const yest = new Date(now);
  yest.setDate(now.getDate() - 1);
  const yy = yest.getFullYear();
  const ym = String(yest.getMonth() + 1).padStart(2, '0');
  const yd = String(yest.getDate()).padStart(2, '0');
  previousKey = `${yy}-${ym}-${yd}`;
} else if (selectedTimeRange === '7days') {
  const getISOWeek = (date) => {
    const target = new Date(date.valueOf());
    const dayNr = (date.getUTCDay() + 6) % 7;
    target.setUTCDate(target.getUTCDate() - dayNr + 3);
    const firstThursday = target.valueOf();
    target.setUTCMonth(0, 1);
    if (target.getUTCDay() !== 4) {
      target.setUTCMonth(0, 1 + ((4 - target.getUTCDay()) + 7) % 7);
    }
    return 1 + Math.ceil((firstThursday - target) / 604800000);
  };
  const yy = now.getUTCFullYear();
  const wn = getISOWeek(now);
  currentKey = `${yy}-W${String(wn).padStart(2, '0')}`;
  const lastWeek = new Date(now);
  lastWeek.setUTCDate(now.getUTCDate() - 7);
  const ly = lastWeek.getUTCFullYear();
  const lwn = getISOWeek(lastWeek);
  previousKey = `${ly}-W${String(lwn).padStart(2, '0')}`;
} else if (selectedTimeRange === '30days' || selectedTimeRange === '60days' || selectedTimeRange === '90days') {
  // month-to-month comparison for floor price, like RN
  const yy = now.getUTCFullYear();
  const m  = String(now.getUTCMonth() + 1).padStart(2, '0');
  currentKey = `${yy}-${m}`;
  const lastMonth = new Date(now);
  lastMonth.setUTCMonth(now.getUTCMonth() - 1);
  const ly = lastMonth.getUTCFullYear();
  const lm = String(lastMonth.getUTCMonth() + 1).padStart(2, '0');
  previousKey = `${ly}-${lm}`;
}


    return assets
      .map((asset) => {
        const { current, previous } = getVolumeForRange(asset);
        const diff =
          previous > 0
            ? ((current - previous) / previous) * 100
            : current > 0
            ? 100
            : 0;

        const fh = asset.floorPriceHistory || {};
        let rangeHistory =
          selectedTimeRange === 'today'
            ? fh.daily || {}
            : selectedTimeRange === '7days'
            ? fh.weekly || {}
            : fh.monthly || {};

        const hasCurr = rangeHistory[currentKey] !== undefined;
        const hasPrev = rangeHistory[previousKey] !== undefined;
        let floorPriceDiff = 0;
        if (hasCurr) {
          const cf = rangeHistory[currentKey];
          if (hasPrev) {
            const pf = rangeHistory[previousKey];
            floorPriceDiff =
              pf > 0 ? ((cf - pf) / pf) * 100 : cf > 0 ? 100 : 0;
          } else {
            floorPriceDiff = 100;
          }
        }

        return {
          ...asset,
          currentVolume: current,
          diff,
          floorPriceDiff,
          creationDate:
            asset.creationDate?.toDate?.() instanceof Date
              ? asset.creationDate.toDate()
              : new Date(0),
        };
      })
      .sort((a, b) => {
        if (sortOption === 'salesVolume') {
          return (
            b.currentVolume - a.currentVolume ||
            b.creationDate - a.creationDate
          );
        }
        if (sortOption === 'salesVolumePercent') {
          return b.diff - a.diff || b.creationDate - a.creationDate;
        }
        if (sortOption === 'floorPrice') {
          return (
            getFloorPrice(b) - getFloorPrice(a) ||
            b.creationDate - a.creationDate
          );
        }
        // floorPricePercent
        return (
          b.floorPriceDiff - a.floorPriceDiff ||
          b.creationDate - a.creationDate
        );
      });
  }, [
    assets,
    getVolumeForRange,
    sortOption,
    getFloorPrice,
    selectedTimeRange,
  ]);

  const handleLikePress = async (assetId) => {
    try {
      const summaryRef = doc(firestore, 'assetsSummary', 'main');
      const userWalletRef = doc(firestore, 'users', userId2, 'wallet', 'main');
      let updatedAssets = [];
      let userLikes = userWallet?.likes || [];
      const already = userLikes.includes(assetId);

      setUserWallet((prev) => ({
        ...prev,
        likes: already
          ? prev.likes.filter((id) => id !== assetId)
          : [...(prev.likes || []), assetId],
      }));

      setAssetsSummary((prev) =>
        prev.map((asset) =>
          asset.id === assetId
            ? {
                ...asset,
                likeCount: already
                  ? Math.max(0, asset.likeCount - 1)
                  : asset.likeCount + 1,
              }
            : asset
        )
      );

      await runTransaction(firestore, async (tx) => {
        const sd = await tx.get(summaryRef);
        if (!sd.exists()) throw new Error('No summary');
        const data = sd.data();
        const lst = data.assets || [];
        const idx = lst.findIndex((a) => a.id === assetId);
        if (idx < 0) throw new Error('Not found');
        const a = lst[idx];
        if (already) {
          a.likeCount = Math.max(0, (a.likeCount || 0) - 1);
          userLikes = userLikes.filter((id) => id !== assetId);
        } else {
          a.likeCount = (a.likeCount || 0) + 1;
          userLikes.push(assetId);
        }
        lst[idx] = a;
        tx.update(summaryRef, { assets: lst });
        tx.set(userWalletRef, { likes: userLikes }, { merge: true });
        updatedAssets = lst;
      });

      setAssetsSummary(updatedAssets);
    } catch (e) {
      console.error('Like error', e);
    }
  };

  const handleNFTPress = (nft) => {
        if (userId2 === "guest") {
      navigate('/login');
      return;
        }
    navigate(`/dat/${nft.id}`, { state: { nft } });
  };

  const handleNavigateSeeAll = () => {
    navigate("marketplace")
  };

const renderCollectionItem = useCallback(
  (asset, index) => {
    const floorPrice = getFloorPrice(asset);
    const vol = asset.currentVolume || 0;
    const diff = asset.diff || 0;
    const fDiff = asset.floorPriceDiff || 0;

    const diffText  = diff  > 0 ? `+${diff.toFixed(1)}%`  : `${diff.toFixed(1)}%`;
    const fDiffText = fDiff > 0 ? `+${fDiff.toFixed(1)}%` : `${fDiff.toFixed(1)}%`;

    const isFloorTab =
      sortOption === 'floorPrice' || sortOption === 'floorPricePercent';

    return (
      <div
        key={asset.id}
        style={styles.collectionRow}
        onClick={() => handleNFTPress(asset)}
      >
        {/* Rank number (keeps all rows aligned) */}
        <div style={styles.rankNumber}>{index + 1}</div>

        {/* Rounded image */}
        <img src={asset.ArtworkURL} style={styles.collectionImage} alt="" />

        {/* Name + sublabel */}
        <div style={styles.collectionTextContainer}>
          <div style={styles.collectionName} title={asset.SongName}>
            {asset.SongName || 'Unnamed'}
          </div>
          <div style={styles.collectionFloorPrice}>
            {isFloorTab
              ? `Sales Volume: $${vol.toLocaleString()}`
              : `Floor Price: $${floorPrice.toLocaleString()}`}
          </div>
        </div>

        {/* Value + percentage change */}
        <div style={styles.collectionVolumeContainer}>
          <div style={styles.collectionVolume}>
            ${(isFloorTab ? floorPrice : vol).toLocaleString()}
          </div>
          <div
            style={{
              ...styles.collectionVolumeChange,
              color: (isFloorTab ? fDiff : diff) >= 0 ? '#37FF8B' : '#FF4949',
            }}
          >
            {isFloorTab ? fDiffText : diffText}
          </div>
        </div>
      </div>
    );
  },
  [getFloorPrice, handleNFTPress, sortOption]
);


const renderItem2 = useCallback(
  (asset, idx) => {
    if (asset === 'loading') {
      return (
        <div key={`load-${idx}`} style={styles.card}>
          <div style={{ fontSize: '16px' }}>Loading...</div>
        </div>
      );
    }
    const editions = asset.editions?.filter((e) => e.isForSale) || [];
    const sel = editions.length ? editions[editions.length - 1] : null;
    const editionNum = sel?.edition || 1;
    const editionPrice = sel?.price || asset.cost;

    return (
      <div
        key={asset.id}
        style={styles.card}
        onClick={() => handleNFTPress(asset)}
      >
        <div
          style={{
            ...styles.cardBackground,
            backgroundImage: `url(${DATbgcard})`,
          }}
        >
          {asset.ArtworkURL && (
            <img
              src={asset.ArtworkURL}
              style={styles.nftImage2}
              alt=""
            />
          )}
          <div style={{ ...styles.nftId, fontSize: '11px', marginLeft: '3px' }}>
            {`${asset.SongName} #${editionNum}`}
          </div>
          <div style={styles.priceContainer}>
            <div style={{ ...styles.price, fontSize: '11px', marginLeft: '3px' }}>
              ${editionPrice?.toLocaleString()}
            </div>
          </div>
          <div
            style={styles.iconButtonLike}
            onClick={(e) => {
              e.stopPropagation();
              handleLikePress(asset.id);
            }}
          >
            <div style={styles.likeButtonContainer}>
              <img
                src={
                  userWallet?.likes?.includes(asset.id)
                    ? LikeIconActive
                    : LikeIcon
                }
                style={styles.iconImage}
                alt=""
              />
              <div style={{ ...styles.likeCount, fontSize: '12px' }}>
                {asset.likeCount || 0}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  },
  [handleNFTPress, handleLikePress, userWallet]
);

const topTenAssets = collectionData.slice(0, 10);
const displayedAssets = showMoreCollections
  ? topTenAssets
  : topTenAssets.slice(0, 4);

return (
  <div style={styles.fullScreen}>
    <img src={BlankBG} style={styles.fullScreenImage} alt="" />
    <div
      style={{
        position: 'absolute',
        alignSelf: 'center',
        width: '100%',
        height: '100%',
        zIndex: 2,
        overflowY: 'auto',
        marginTop: '-4px',
        paddingTop: scrollAreaTopPadding,
        paddingBottom: '264px',
      }}
    >
      <div style={styles.collectionHeader}>
        <div style={styles.collectionTitle}>
          <span style={{ fontSize: '18px', color: '#ffffff' }}>THE DAT </span>
          <span style={{ fontSize: '18px', color: '#ffffff' }}>CHART</span>
        </div>
{/* ─── date-range selector ─── */}
<div style={styles.dropdownWrapper}>
  {/* transparent <select> fills the area */}
  <select
  value={selectedTimeRange}
  onChange={e => setSelectedTimeRange(e.target.value)}
  style={{
    position:  'relative',
    zIndex:    2,
    appearance:'none',
    background:'none',
    marginBottom: '9px',
    border:    'none',
    color:     '#62fbfa',
    fontSize:  '18px',

    /* ↓ NEW ↓ */
    direction: 'rtl',          // makes content align to the right edge
    textAlign: 'right',        // keeps dropdown list readable
    padding:   '4px 25px 4px 24px',// leave 24 px on the *left* for arrow gap
    /* ↑ NEW ↑ */

    cursor:    'pointer',
  }}
>
<option value="30days">Last 30 Days</option>
<option value="60days">Last 60 Days</option>
<option value="90days">Last 90 Days</option>
</select>


  {/* static arrow – always white – no pointer events */}
  <img
    src={DownArrow}
    style={{
      position:   'absolute',
      right:      '4px',
      top:        '41%',
      width:      '14px',
      height:     '14px',
      translate:  '0 -50%',
      marginBottom: '5px',
      pointerEvents:'none',
      filter:     'invert(100%) brightness(200%)',  // forces pure white
    }}
    alt=""
  />
</div>

      </div>

      <div style={{ display: 'flex', justifyContent: 'space-evenly', marginTop: '5px', marginBottom: '12px', margin: '0 12px' }}>
        {[
          { key: 'salesVolume', label: 'Sales Volume' },
          { key: 'salesVolumePercent', label: 'Sales Volume %' },
          { key: 'floorPrice', label: 'Floor Price' },
          { key: 'floorPricePercent', label: 'Floor Price %' },
        ].map((opt) => (
          <button
            key={opt.key}
            onClick={() => setSortOption(opt.key)}
            style={{
              padding: '2px 9px',
              borderRadius: '8px',
              backgroundColor: sortOption === opt.key ? 'rgba(98,251,250,0.25)' : 'rgba(255,255,255,0.1)',
              border: 'none',
              cursor: 'pointer',
              marginBottom: '10px',
              paddingTop: '5px'
            }}
          >
            <span style={{ color: '#fff', fontSize: '13.3px' }}>
              {opt.label}
            </span>
          </button>
        ))}
      </div>

<div style={{ marginTop: '12px', margin: '0 16px' }}>
  {displayedAssets.map((a, idx) => renderCollectionItem(a, idx))}
</div>


{topTenAssets.length > 3 && (
  <button
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '6px auto 0',
      padding: '6px 10px',
      borderRadius: '8px',
      backgroundColor: 'rgba(255,255,255,0.1)',
      border: 'none',
      cursor: 'pointer',
    }}
    onClick={() => setShowMoreCollections(!showMoreCollections)}
  >
    <span style={{ color: '#ffffff', fontSize: '15px', marginRight: '6px' }}>
      {showMoreCollections ? 'Less' : 'More'}
    </span>
    <img
  src={DownArrow}
  style={{
    width:  '14px',
    height: '14px',
    transform: showMoreCollections ? 'rotate(180deg)' : 'rotate(0deg)',
    transition: 'transform 0.25s ease',
    filter: 'invert(100%) brightness(200%)',          // white
  }}
  alt=""
/>


  </button>
)}


      <div style={{ display: 'flex', justifyContent: 'space-between', margin: '0 16px', marginTop: '2px' }}>
        <div style={{ color: '#fff', fontSize: '18px', fontWeight: 'bold',  }}>
          Marketplace
        </div>
        <button
          onClick={handleNavigateSeeAll}
          style={{
            background: 'none',
            border: 'none',
            color: '#62fbfa',
            fontSize: '18px',

            cursor: 'pointer',
          }}
        >
          See All
        </button>
      </div>

      <div
        style={{
          display: 'flex',
          overflowX: 'auto',
          padding: '8px 13px',
          gap: '1px',
          maxWidth: '480px',
          margin: '0 auto',
        }}
      >
{Array.isArray(assets)
  ? [...assets]
      .sort((a, b) => {
        // primary: releaseTimestamp desc; tie-break: creationDate desc
        const r = rtMillis(b.releaseTimestamp) - rtMillis(a.releaseTimestamp);
        if (r !== 0) return r;
        const da = a.creationDate?.toDate?.() || new Date(0);
        const db = b.creationDate?.toDate?.() || new Date(0);
        return db - da;
      })
      .map((a, i) => renderItem2(a, i))
  : null}

      </div>
    </div>

    <div style={{ height: bottomAreaHeight, backgroundColor: 'transparent' }} />
  </div>
);
};

const styles = {
  fullScreen: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    overflow: 'hidden',
    width: '100%',
    marginTop: '5px',
    paddingLeft: '10px',
    paddingRight: '10px',
  },
  fullScreenImage: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 0,
  },
  collectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    margin: '0 16px',
    marginTop: '10px',
  },
  collectionTitle: {
    color: 'rgba(98,251,250,0.25)',
    fontSize: '18px',
    fontWeight: 'bold',
    marginBottom: '3px',

  },
  dropdownWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  collectionRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '6px 0',
    borderRadius: '10px',
    cursor: 'pointer',
  },
  collectionImage: {
    width: '36px',
    height: '36px',
    borderRadius: '25px',
    marginRight: '8px',
    objectFit: 'cover',
  },
  collectionTextContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  collectionName: {
    color: '#ffffff',
    fontWeight: 600,
    marginBottom: '2px',

  },
  collectionFloorPrice: {
    color: '#fff',
    marginTop: '3px',
    fontWeight: 'bold',

  },
  collectionVolumeContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
  },
  collectionVolume: {
    color: '#ffffff',
    fontWeight: 600,

  },
  collectionVolumeChange: {
    fontWeight: 600,

  },
  card: {
    flex: '0 0 auto',
    width: '150px',   // same responsive width we used for “Liked DATS”
    aspectRatio: '3/4',
    position: 'relative',
    
    border: 0,
    padding: 0,
    borderRadius: '12px',
    overflow: 'hidden',
    cursor: 'pointer',
    background: 'transparent',
  },
  
  cardBackground: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  },
  
  nftImage2: {
    position: 'absolute',
    top: 0,
    left: '4px',
    width: '95.5%',
    height: '70%',          // fills the upper 70 % of the card
    objectFit: 'cover',     // was “contain” – switch to “cover”
    borderRadius: '10px 10px 0 0',
  },
  
  nftId: {
    position: 'absolute',
    top: 'calc(70% + 4px)', // sits right under the artwork
    left: '8px',
    right: '8px',
    color: '#FFFFFF',

    fontWeight: 600,
fontSize: '14px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  
  priceContainer: {},
  price: {
    position: 'absolute',
    top: 'calc(70% + 23px)', // a bit below the title
    left: '8px',
    color: '#FFFFFF',

    fontWeight: 600,
fontSize: '14px',
  },
  
  iconButtonLike: {
    position: 'absolute',
    bottom: '3px',
    left: '8px',
    padding: 0,
    cursor: 'pointer',
    marginLeft: '3px'
  },
  
  likeButtonContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  iconImage: {
    width: '13px',
    height: '13px',
    marginRight: '3px',
  },
  likeCount: {
    color: 'white',
    marginTop: '2px',

  },

  price: {
    position: 'absolute',
    top: 'calc(70% + 20px)',   // was 23px, so it’s moved up 5px
    left: '8px',
    color: '#FFFFFF',

    fontWeight: 600,
fontSize: '14px',
  },

  iconButtonLike: {
    position: 'absolute',
    bottom: '4px',            // was 3px, so like button sits 9px lower
    left: '8px',
    padding: 0,
    cursor: 'pointer',
    marginLeft: '3px'
  },
    collectionRow: {
    display: 'flex',
    alignItems: 'center',
    padding: `${Math.max(6, height * 0.007)}px ${Math.max(10, width * 0.02)}px`,
    borderRadius: '10px',
    background: 'rgba(24,27,45,0.8)',
    border: '1px solid rgba(98,251,250,0.15)',
    marginBottom: `${Math.max(6, height * 0.008)}px`,
    boxShadow: '0 3px 6px rgba(0,0,0,0.25)',
    cursor: 'pointer',
  },

  rankNumber: {
    color: '#ffffff',
    fontWeight: 'bold',
    width: '30px',
    textAlign: 'center',
    marginRight: 0,
fontSize: '14px',
  },

  collectionImage: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',               // round image like RN
    marginRight: `10px`,
    objectFit: 'cover',
  },

  collectionTextContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },

  collectionName: {
    color: '#ffffff',
    fontWeight: 600,
    marginBottom: '2px',
fontSize: '13px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },

  collectionFloorPrice: {
    color: '#fff',
    marginTop: `${Math.max(2, height * 0.003)}px`,
    fontWeight: 'bold',
fontSize: '13px',
  },

  collectionVolumeContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    marginLeft: '8px',
  },

  collectionVolume: {
    color: '#ffffff',
    fontWeight: 600,
fontSize: '14px',
  },

  collectionVolumeChange: {
    fontWeight: 600,
fontSize: '14px',
  },
};


export default MusicHome;
